import React, { useState } from 'react';
import {
  Check,
  CheckCircle2,
  Contact,
  Copy,
  Loader2,
  Pencil,
  ShieldCheck,
  StickyNote,
  UserPlus,
  X,
} from 'lucide-react';
import { toast } from 'react-toastify';

import { sourceIdentity } from './boardUtils';
import { VA_GOV_TEXT, toldUsRows, typedNoteOf } from '../../../lib/leadFacts';

// The premium lead-detail language, shared verbatim between the Onboarding
// Support board's slide-over and the Lead Handoffs workspace (Nic 2026-08-13:
// "All the lead information should also be available to the Onboarding
// Support... and as premium as it looks there"). The dark: variants are inert
// on light-only pages.

export const CopyValue = ({ text }) => {
  const [copied, setCopied] = useState(false);
  return (
    <button
      type="button"
      title="Copy"
      onClick={async () => {
        try {
          await navigator.clipboard.writeText(String(text));
          setCopied(true);
          setTimeout(() => setCopied(false), 1200);
        } catch {
          /* clipboard unavailable — silently skip */
        }
      }}
      className={`shrink-0 transition-colors ${
        copied
          ? 'text-emerald-500'
          : 'text-slate-300 hover:text-slate-500 dark:text-slate-600 dark:hover:text-slate-400'
      }`}
    >
      {copied ? <Check size={13} /> : <Copy size={13} />}
    </button>
  );
};

export const InfoRow = ({ label, value, copyable = false, placeholder }) =>
  value ? (
    <div className="flex items-center justify-between gap-3 py-1.5 text-sm">
      <span className="shrink-0 text-slate-400 dark:text-slate-500">
        {label}
      </span>
      <span className="inline-flex min-w-0 items-center gap-1.5 text-right font-medium text-slate-700 dark:text-slate-200">
        <span className="truncate">{value}</span>
        {copyable ? <CopyValue text={value} /> : null}
      </span>
    </div>
  ) : placeholder ? (
    // An absent value usually just hides the row; a placeholder keeps the
    // row and states the absence (e.g. "No email on record").
    <div className="flex items-center justify-between gap-3 py-1.5 text-sm">
      <span className="shrink-0 text-slate-400 dark:text-slate-500">
        {label}
      </span>
      <span className="truncate text-right italic text-slate-400 dark:text-slate-500">
        {placeholder}
      </span>
    </div>
  ) : null;

// Card shell for the intake info groups — same surface language as the board
// tiles (white, hairline border) so the section reads designed, not appended.
export const InfoCard = ({ title, icon: Icon, children }) => (
  <div className="mb-3 overflow-hidden rounded-xl bg-white shadow-[0_1px_2px_rgba(15,23,42,0.04),0_2px_8px_rgba(15,23,42,0.05)] dark:!bg-surface-card dark:ring-1 dark:ring-white/10">
    <div className="flex items-center gap-1.5 border-b border-[#EDF1F6] px-3.5 py-2 text-[11px] font-semibold uppercase tracking-wider text-slate-400 dark:border-white/10">
      {Icon ? <Icon size={12} className="shrink-0" /> : null}
      {title}
    </div>
    <div className="divide-y divide-slate-100 px-3.5 py-1 dark:divide-white/5">
      {children}
    </div>
  </div>
);

// Inline add/edit for the email row (the old Leads page pattern). Rendered
// only when the caller passes onSaveEmail. Both slide-overs now do: Lead
// Handoffs because Send Invite needs an address (Nic 2026-08-17), and the
// board because the specialist owns the booking and a phone-only lead who
// agrees to a Calendly link on the call has nowhere to receive it
// (Nic 2026-08-20, reversing the read-only call). The two saves reach
// different routes — Handoffs has leads:manage, the board does not — but
// land on the same validated, deduped updateLead.
const EmailInfoRow = ({ email, onSaveEmail }) => {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState('');
  const [saving, setSaving] = useState(false);

  const save = async () => {
    const value = draft.trim();
    if (!value || saving) return;
    setSaving(true);
    try {
      await onSaveEmail(value);
      setEditing(false);
      setDraft('');
    } catch (err) {
      toast.error(err?.response?.data?.msg || 'Could not save the email.');
    } finally {
      setSaving(false);
    }
  };

  if (editing) {
    return (
      <div className="flex items-center justify-between gap-2 py-1.5 text-sm">
        <span className="shrink-0 text-slate-400 dark:text-slate-500">
          Email
        </span>
        <span className="inline-flex min-w-0 items-center gap-1.5">
          <input
            type="email"
            // eslint-disable-next-line jsx-a11y/no-autofocus
            autoFocus
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') save();
              if (e.key === 'Escape') {
                // Cancel ONLY the edit: the handoff slide-over closes on a
                // window-level Escape, and this keypress must not reach it.
                e.stopPropagation();
                setEditing(false);
              }
            }}
            placeholder="name@example.com"
            // dark:!bg-white/5 — Bootstrap's .bg-white is !important and beat
            // the plain dark: variant, leaving the field white in dark mode.
            className="h-7 w-44 min-w-0 rounded-md border border-slate-200 bg-white px-2 text-sm text-slate-700 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-400 dark:border-white/10 dark:!bg-white/5 dark:text-slate-200 dark:placeholder:text-slate-500"
          />
          <button
            type="button"
            onClick={save}
            disabled={!draft.trim() || saving}
            className="shrink-0 text-emerald-500 hover:text-emerald-600 disabled:opacity-40"
            aria-label="Save email"
          >
            {saving ? (
              <Loader2 size={15} className="animate-spin" />
            ) : (
              <CheckCircle2 size={15} />
            )}
          </button>
          <button
            type="button"
            onClick={() => setEditing(false)}
            className="shrink-0 text-slate-400 hover:text-slate-600 dark:hover:text-slate-300"
            aria-label="Cancel"
          >
            <X size={15} />
          </button>
        </span>
      </div>
    );
  }

  return (
    <div className="flex items-center justify-between gap-3 py-1.5 text-sm">
      <span className="shrink-0 text-slate-400 dark:text-slate-500">Email</span>
      <span className="inline-flex min-w-0 items-center gap-1.5 text-right">
        {email ? (
          <>
            <span className="truncate font-medium text-slate-700 dark:text-slate-200">
              {email}
            </span>
            <CopyValue text={email} />
          </>
        ) : (
          <span className="truncate italic text-slate-400 dark:text-slate-500">
            No email on record
          </span>
        )}
        <button
          type="button"
          onClick={() => {
            setDraft(email || '');
            setEditing(true);
          }}
          title={email ? 'Edit email' : 'Add email'}
          className="shrink-0 text-slate-300 transition-colors hover:text-slate-500 dark:text-slate-600 dark:hover:text-slate-400"
          aria-label={email ? 'Edit email' : 'Add email'}
        >
          <Pencil size={13} />
        </button>
      </span>
    </div>
  );
};

export const formatPhone = (p) => {
  const d = String(p || '')
    .replace(/\D/g, '')
    .replace(/^1/, '');
  return d.length === 10
    ? `(${d.slice(0, 3)}) ${d.slice(3, 6)}-${d.slice(6)}`
    : p;
};

export const triState = (v) => (v === true ? 'Yes' : v === false ? 'No' : null);

// The REAL Envoy snapshot buckets conditions by claimed rating
// ({"10": ["Tinnitus"], ...}); older/demo shapes carry a flat array. Render
// either faithfully.
export const conditionsText = (snapshot) => {
  if (Array.isArray(snapshot.conditions)) return snapshot.conditions.join(', ');
  if (typeof snapshot.conditions === 'string') return snapshot.conditions;
  const byRating = snapshot.conditionsByRating;
  if (byRating && typeof byRating === 'object') {
    const parts = Object.entries(byRating)
      .filter(([, list]) => Array.isArray(list) && list.length)
      .sort((a, b) => Number(b[0]) - Number(a[0]))
      .map(([rating, list]) => `${list.join(', ')} (${rating}%)`);
    return parts.join(' · ') || null;
  }
  return null;
};

// Source-specific intake block: everything a specialist should read BEFORE
// the first call. Field names mirror the PRODUCTION intake shapes exactly
// (createPartnerLead / createReferralLead in leadsController +
// envoyLeadSnapshot.js), with fallbacks for older demo data.
export const LeadDetailSections = ({ lead, onSaveEmail = null }) => {
  const snapshot = lead.leadMeta?.veteranSnapshot;
  const referredBy = lead.leadMeta?.referredBy;
  const rating =
    snapshot?.currentVaPercentage != null
      ? `${snapshot.currentVaPercentage}%`
      : snapshot?.currentRating || null;
  const location = snapshot?.location?.city
    ? [snapshot.location.city, snapshot.location.state]
        .filter(Boolean)
        .join(', ')
    : [
        snapshot?.city || lead.leadMeta?.city,
        snapshot?.state || lead.leadMeta?.state,
      ]
        .filter(Boolean)
        .join(', ') || null;
  // The website form has no free-text box: its "note" is only its answers
  // joined into a line, which the snapshot card now shows (2026-10-14).
  const intakeNote =
    lead.leadNotes ||
    typedNoteOf({
      sourceSystem: lead.leadSourceSystem,
      notes: lead.leadMeta?.notes || snapshot?.notes,
    });
  const vaGovText = VA_GOV_TEXT[lead.leadVaGovAccount] || null;
  const formSnapshotRows = [
    ...toldUsRows({
      isVeteran: lead.leadMeta?.isVeteran,
      rank: lead.leadMeta?.rank,
      vaGovAccount: lead.leadVaGovAccount,
    }),
    ...(location ? [['Location', location]] : []),
  ];

  const source = sourceIdentity(lead);
  // A claimed lead's leadCreatedOn is the claim (its clock starts there), so
  // Arrived reads the true arrival and the claim gets its own row
  // (Nic 2026-10-14).
  const arrivedAt =
    (lead.leadBacklogClaimedAt && lead.leadOriginalCreatedOn) ||
    lead.leadCreatedOn;

  return (
    <>
      <InfoCard title="Contact" icon={Contact}>
        <InfoRow label="Phone" value={formatPhone(lead.leadPhone)} copyable />
        {onSaveEmail ? (
          <EmailInfoRow email={lead.leadEmail} onSaveEmail={onSaveEmail} />
        ) : (
          <InfoRow
            label="Email"
            value={lead.leadEmail}
            copyable
            placeholder="No email on record"
          />
        )}
        <InfoRow
          label="Arrived"
          value={new Date(arrivedAt).toLocaleString([], {
            month: 'short',
            day: 'numeric',
            ...(new Date(arrivedAt).getFullYear() !== new Date().getFullYear()
              ? { year: 'numeric' }
              : {}),
            hour: 'numeric',
            minute: '2-digit',
          })}
        />
        {/* Arrived outside working hours, or pushed (2026-08-15): the clock
            start is a different instant from the arrival, and the difference
            is the whole reason Take is refused, so say it here rather than
            leaving the disabled button to explain itself. */}
        {lead.leadCadenceStartAt &&
        new Date(lead.leadCadenceStartAt) > new Date() ? (
          <InfoRow
            label="Starts"
            value={new Date(lead.leadCadenceStartAt).toLocaleString([], {
              weekday: 'short',
              hour: 'numeric',
              minute: '2-digit',
            })}
          />
        ) : null}
        {/* When it was claimed, named by where from (2026-10-14): an event
            lead from Events, anything else from the imported backlog. */}
        {lead.leadBacklogClaimedAt && lead.leadOriginalCreatedOn ? (
          <InfoRow
            label={
              lead.partnerType === 'event'
                ? 'Claimed from Events'
                : 'Claimed from backlog'
            }
            value={new Date(lead.leadBacklogClaimedAt).toLocaleString([], {
              month: 'short',
              day: 'numeric',
              hour: 'numeric',
              minute: '2-digit',
            })}
          />
        ) : null}
      </InfoCard>
      {/* One uniform Source section for EVERY lead — who sent them and what
          kind of channel that is; referrer contact folds in when it exists. */}
      <InfoCard title="Source" icon={UserPlus}>
        <InfoRow label="From" value={source.label} />
        <InfoRow
          label="Type"
          value={
            source.detail && source.detail !== source.label
              ? source.detail
              : null
          }
        />
        {referredBy && typeof referredBy === 'object' ? (
          <>
            <InfoRow label="Email" value={referredBy.email} copyable />
            <InfoRow
              label="Phone"
              value={referredBy.phone ? formatPhone(referredBy.phone) : null}
            />
          </>
        ) : null}
      </InfoCard>
      {/* Our own forms (website, referral, partner, event) answer a few of
          the same questions; they get the same card (Nic 2026-10-14). */}
      {!snapshot && formSnapshotRows.length ? (
        <InfoCard title="Veteran snapshot" icon={ShieldCheck}>
          {formSnapshotRows.map(([label, value]) => (
            <InfoRow key={label} label={label} value={value} />
          ))}
        </InfoCard>
      ) : null}
      {snapshot ? (
        <InfoCard title="Veteran snapshot" icon={ShieldCheck}>
          <InfoRow
            label="Branch"
            value={
              [snapshot.branch, snapshot.rankPaygrade]
                .filter(Boolean)
                .join(' · ') || null
            }
          />
          <InfoRow label="Current rating" value={rating} />
          <InfoRow
            label="Service connected"
            value={triState(snapshot.serviceConnected)}
          />
          <InfoRow
            label="Combat veteran"
            value={triState(snapshot.combatVeteran)}
          />
          <InfoRow label="Open claim" value={snapshot.openClaim} />
          {/* Leads stored before the snapshot carried it still have the raw
              answer in customAttributes. */}
          <InfoRow
            label="VA.gov account"
            value={
              snapshot.vaGovAccount ||
              (typeof lead.leadMeta?.customAttributes?.va_gov_account ===
              'string'
                ? lead.leadMeta.customAttributes.va_gov_account.trim()
                : null) ||
              vaGovText ||
              null
            }
          />
          <InfoRow
            label="Medical records"
            value={triState(snapshot.hasMedicalServiceRecords)}
          />
          <InfoRow label="Conditions" value={conditionsText(snapshot)} />
          <InfoRow label="Location" value={location} />
        </InfoCard>
      ) : null}
      {intakeNote ? (
        <InfoCard title="Intake note" icon={StickyNote}>
          <div className="whitespace-pre-wrap py-2 text-sm leading-relaxed text-slate-600 dark:text-slate-300">
            {intakeNote}
          </div>
        </InfoCard>
      ) : null}
    </>
  );
};
